import { APP_VERSION, OS_NAME } from '../config/env';
import { prefs } from '../db/prefs';
import { getProvisionKey } from './provisionKey';
import type { DeviceMeta } from '../types/api';

// Best-effort device metadata for pairing/login. The browser cannot expose
// MAC/IP/manufacturer like Android, so we report what is available.

export function getScreenSize(): { width: number; height: number } {
  const dpr = window.devicePixelRatio || 1;
  return {
    width: Math.round(window.screen.width * dpr),
    height: Math.round(window.screen.height * dpr),
  };
}

export async function getDeviceMeta(): Promise<DeviceMeta> {
  const size = getScreenSize();
  // Report the effective resolution for the chosen orientation: a 90/270 install
  // swaps width/height so the dashboard records the orientation actually shown.
  const rotation = ((((await prefs.getRotation()) % 360) + 360) % 360);
  const rotated = rotation === 90 || rotation === 270;
  const width = rotated ? size.height : size.width;
  const height = rotated ? size.width : size.height;

  const provisioningKey = getProvisionKey();

  return {
    os: OS_NAME,
    os_version: navigator.userAgent,
    app_version: APP_VERSION,
    screen_width: String(width),
    screen_height: String(height),
    model: navigator.platform || 'Browser',
    manufacturer: detectManufacturer(),
    ...(provisioningKey ? { provisioning_key: provisioningKey } : {}),
  };
}

function detectManufacturer(): string {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('tizen')) return 'Samsung';
  if (ua.includes('web0s') || ua.includes('webos')) return 'LG';
  if (ua.includes('chrome')) return 'Chromium';
  return 'Web';
}

// Approximate storage usage for update-stats.
export async function getStorageEstimate(): Promise<{ used: number; quota: number }> {
  if (navigator.storage?.estimate) {
    const est = await navigator.storage.estimate();
    return { used: est.usage ?? 0, quota: est.quota ?? 0 };
  }
  return { used: 0, quota: 0 };
}
